import styled, { css } from 'styled-components';

import { rem, hiddenPrint } from 'approot/shared/style';
import {
  grey1,
  grey2,
  grey3,
  grey5,
  grey6,
  grey7,
  grey8,
  grey9,
  purple6,
  purple7,
  purple8,
  red1,
  red6,
  red7,
} from 'approot/shared/styles/colours.style';
import {
  standardInsetBoxShadow,
  standardFocusState,
  negativeInsetBoxShadow,
} from 'approot/shared/styles/borders.style';
import { spacing8 } from '../styles/spacing.style';

export type RoundIconButton = {
  selected: boolean;
  rightSpacing?: boolean;
  bottomSpacing?: boolean;
  isCardButton?: boolean;
  isPlusIcon?: boolean;
  isNegative?: boolean;
  isDanger?: boolean;
  $noBorder?: boolean;
};

export const roundButtonBase = css<RoundIconButton>`
  background: ${grey1};
  border-radius: 100%;
  border: ${({ $noBorder }) => ($noBorder ? 'none' : `1px solid ${grey5}`)};
  cursor: pointer;
  width: ${rem(40)};
  height: ${rem(40)};
  line-height: 0px;
  margin: 0;
  fill: ${grey1};
  padding: 0;
  text-align: center;

  &:hover {
    border-color: ${grey9};
    background: ${grey2};
  }

  ${standardFocusState}

  &:active {
    background: ${grey2};
    border-color: ${grey5};

    ${standardInsetBoxShadow}
    &:focus {
      ${standardInsetBoxShadow}
    }
  }
  ${hiddenPrint};
`;

export const RoundIconButtonStyled = styled.button<RoundIconButton>`
  ${roundButtonBase}

  ${props =>
    props.rightSpacing &&
    `
    margin-right: ${rem(spacing8)};
  `}

  ${props =>
    props.bottomSpacing &&
    `
    margin-bottom: ${rem(spacing8)};
  `}

  ${props =>
    props.isCardButton &&
    `
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.08), 0px 7px 8px rgba(0, 0, 0, 0.08);
  `}

  ${props =>
    !props.selected &&
    `
      &:hover {
        svg {
          stroke: ${grey8};  
        }
      }

      &:focus {
        svg {
          stroke: ${grey9};
        }
      }

      &:active {
        border: 1px solid ${grey5};
        background-color: ${grey2};

        svg {
          stroke: ${grey9};
          fill: ${grey2};
        }
      }

      &:disabled,
      &.disabled {
        border: 1px solid ${grey5} !important;
        background-color: ${grey3} !important;
        box-shadow: none !important;
        cursor: not-allowed !important;

        svg {
          fill: ${grey3} !important;
          stroke: ${grey6} !important;
        }
      }
  `};

  ${props =>
    props.selected &&
    `
    svg {
      stroke: ${purple7};
      fill: ${purple7};
    }
    
    &:hover {
      svg {
        fill: ${purple6};
        stroke: ${purple6};
      }
    }

    &:focus {
      svg {
        fill: ${purple7};
        stroke: ${purple7};
      }
    }

    &:active {
      border: 1px solid ${grey7};
      background-color: ${grey2};
      svg {
        fill: ${purple8};
        stroke: ${purple8};
      }
    }
  `};

  ${props =>
    props.selected &&
    props.isPlusIcon &&
    `
      // sets the inner plus to white but keeps the outer circle purple
      path:not(:first-of-type) {
        stroke: white;
      }
    `};

  ${props =>
    props.isNegative &&
    `
      &:hover {
        background-color: ${red1};
        svg {
          stroke: ${red7};
        }
      }

      &:focus {
        background-color: ${red1};
        svg {
          stroke: ${red7};
        }
      }

      &:active {
        background-color: ${red1};
        border: 1px solid ${red1};
        svg {
          fill: ${red7};
          stroke: ${red7};
        }
        &:focus {
          ${negativeInsetBoxShadow}
          svg {
            stroke: ${red7};
          }
        }
      }
  `};

  ${props =>
    props.isDanger &&
    `
      fill: ${grey1};
      background-color: ${red7};
      svg {
        stroke: ${red7};
      }

      &:hover {
        background-color: ${red6};
        svg {
          stroke: ${red6};
        }
      }

      &:focus {
        background-color: ${red6};
        border: 2px solid ${grey9};
        svg {
          stroke: ${red6};
        }
      }

      &:active {
        background-color: ${red1};
        svg {
          stroke: ${red6};
        }
        &:focus {
          svg {
            stroke: ${red7};
          }
          background-color ${red7};
          ${negativeInsetBoxShadow}
        }
      }
  `};
`;
